import { useMemo } from 'react';
import { ArrowUpDown, ArrowDown, ArrowUp } from 'lucide-react';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@staff-app/ui';
import { SORT_DIRECTION, formatModel } from '@staff-app/shared-types';
import { formatDateMedium } from '@/lib/format-date';
import { safeDistanceToNow } from '@/lib/safe-format';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';
import type { LiveDeviceStatus } from '../hooks/useDeviceHeartbeats';
import { useMdmDevicesSortStore, type MdmDeviceSortField } from '../stores/mdm-devices-sort-store';
import { useMdmDevicesViewStore } from '../stores/mdm-devices-view-store';
import { DeviceStatusDot } from './DeviceStatusDot';

// Phase 7 T070 — labels come from the shared `@staff-app/shared-types/device-labels`
// formatters which take the backend-classified `DeviceClassification`. When an
// older cached device still lacks classification (e.g. an open detail drawer
// during deploy rollover), fall back to an em-dash rather than crashing.
function formatModelSafe(d: MdmDevice): string {
  return d.classification ? formatModel(d.classification) : (d.model ?? '—');
}

interface ColumnSpec {
  id: string;
  labelKey: string;
  /** Optional Tailwind width for this column under the fixed table layout. */
  width?: string;
  render: (device: MdmDevice) => React.ReactNode;
}

interface Props {
  devices: MdmDevice[];
  onRowClick: (deviceId: string) => void;
  /** Click an Assign id → push that content to the staff's own testing devices. */
  onAssignClick?: (contentId: string) => void;
  /** Live status by device UUID (open-api heartbeat); dot falls back to lastHeartBeat. */
  liveStatusByUuid?: Map<string, LiveDeviceStatus>;
}

export function DeviceTable({ devices, onRowClick, onAssignClick, liveStatusByUuid }: Props) {
  const columnConfig = useMdmDevicesViewStore((s) => s.columnConfig);
  const sortField = useMdmDevicesSortStore((s) => s.sortField);
  const sortDirection = useMdmDevicesSortStore((s) => s.sortDirection);
  const setSort = useMdmDevicesSortStore((s) => s.setSort);

  const columnMap = useMemo<Record<string, ColumnSpec>>(
    () => ({
      deviceName: {
        id: 'deviceName',
        labelKey: 'Device name',
        // Primary identifier — give it a larger, responsive share than the
        // evenly-distributed columns so long names stay readable.
        width: 'w-[22%]',
        render: (d) => (
          <span
            className="block truncate font-medium"
            title={d.deviceName ?? undefined}
          >
            {d.deviceName ?? '—'}
          </span>
        ),
      },
      fleet: {
        id: 'fleet',
        labelKey: 'Fleet',
        render: (d) =>
          d.fleetId ? (
            <span className="truncate text-sm">{d.fleetId}</span>
          ) : (
            <span className="text-sm text-muted-foreground">{'Default'}</span>
          ),
      },
      team: {
        id: 'team',
        labelKey: 'Team',
        render: (d) => (
          <span className="block truncate text-sm" title={d.teamName ?? undefined}>
            {d.teamName ?? '—'}
          </span>
        ),
      },
      path: {
        id: 'path',
        labelKey: 'Folder',
        render: (d) => (
          <span className="block truncate text-sm" title={d.path ?? undefined}>
            {d.path ?? '—'}
          </span>
        ),
      },
      os: {
        id: 'os',
        labelKey: 'OS',
        render: (d) => <span className="text-sm">{d.os ?? '—'}</span>,
      },
      osVersion: {
        id: 'osVersion',
        labelKey: 'OS Version',
        render: (d) => (
          <span className="block truncate text-sm" title={d.osVersion ?? undefined}>
            {d.osVersion ?? '—'}
          </span>
        ),
      },
      lastHeartBeat: {
        id: 'lastHeartBeat',
        labelKey: 'Last seen',
        render: (d) => (
          <span className="text-sm text-muted-foreground">
            {d.lastHeartBeat ? safeDistanceToNow(d.lastHeartBeat, { addSuffix: true }, '—') : '—'}
          </span>
        ),
      },
      model: {
        id: 'model',
        labelKey: 'Model',
        // Raw `model` remains available via the browser tooltip for debugging.
        render: (d) => (
          <span className="block truncate text-sm" title={d.model ?? undefined}>
            {formatModelSafe(d)}
          </span>
        ),
      },
      platform: {
        id: 'platform',
        labelKey: 'Platform',
        render: (d) => <span className="text-sm">{d.platform ?? '—'}</span>,
      },
      localAppVersion: {
        id: 'localAppVersion',
        labelKey: 'App version',
        render: (d) => <span className="text-sm">{d.localAppVersion ?? '—'}</span>,
      },
      assignType: {
        id: 'assignType',
        labelKey: 'Assign type',
        render: (d) =>
          d.currentType ? (
            <Badge variant="secondary">{d.currentType}</Badge>
          ) : (
            <span className="text-sm text-muted-foreground">{'—'}</span>
          ),
      },
      assignName: {
        id: 'assignName',
        labelKey: 'Assign name',
        render: (d) => (
          <span className="block truncate text-sm" title={d.currentAssignName ?? undefined}>
            {d.currentAssignName ?? '—'}
          </span>
        ),
      },
      assignId: {
        id: 'assignId',
        labelKey: 'Assign id',
        render: (d) =>
          d.currentAssignId ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAssignClick?.(d.currentAssignId as string);
              }}
              className="cursor-pointer font-mono text-xs text-primary underline-offset-2 hover:underline"
              title="Push this content to my devices"
              data-testid="device-assign-id-push"
            >
              {d.currentAssignId}
            </button>
          ) : (
            <span className="text-xs font-mono text-muted-foreground">{'—'}</span>
          ),
      },
      serialNo: {
        id: 'serialNo',
        labelKey: 'Serial',
        render: (d) => <span className="text-sm">{d.serialNo ?? '—'}</span>,
      },
      UUID: {
        id: 'UUID',
        labelKey: 'UUID',
        render: (d) => (
          <span className="text-xs font-mono text-muted-foreground">{d.UUID ?? '—'}</span>
        ),
      },
      // Staff-only: VPN IP from mdm-devices left-join. Never expose this on
      // apps/web — the backend `StaffDevice` type is the only way to obtain it.
      vpnIp: {
        id: 'vpnIp',
        labelKey: 'VPN IP',
        render: (d) => (
          <span className="text-xs font-mono text-muted-foreground">{d.vpnIp ?? '—'}</span>
        ),
      },
      tags: {
        id: 'tags',
        labelKey: 'Tags',
        render: (d) => (
          <span className="text-xs text-muted-foreground">
            {d.tags && d.tags.length > 0 ? d.tags.join(', ') : '—'}
          </span>
        ),
      },
      tz: {
        id: 'tz',
        labelKey: 'Timezone',
        render: (d) => <span className="text-sm">{d.tz ?? '—'}</span>,
      },
      activationDate: {
        id: 'activationDate',
        labelKey: 'Paired',
        render: (d) => (
          <span className="text-sm text-muted-foreground">
            {formatDateMedium(d.activationDate, '—')}
          </span>
        ),
      },
    }),
    [onAssignClick],
  );

  // Build visible column list in canonical order, pinning status + name.
  const visibleOrderIds = useMemo(() => {
    const pinnedLead = ['deviceName'];
    const visibleSet = new Set(columnConfig.visible);
    // Respect canonical column order, drop status & deviceName (pinned elsewhere).
    const optional = columnConfig.order.filter(
      (id) => id !== 'status' && id !== 'deviceName' && visibleSet.has(id),
    );
    return [...pinnedLead, ...optional];
  }, [columnConfig]);

  const handleHeaderClick = (field: MdmDeviceSortField) => {
    if (sortField === field) {
      setSort(
        field,
        sortDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
      );
    } else {
      setSort(field, SORT_DIRECTION.DESC);
    }
  };

  const renderSortIcon = (field: MdmDeviceSortField) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" aria-hidden />;
    return sortDirection === SORT_DIRECTION.ASC ? (
      <ArrowUp className="ml-1 h-3 w-3" aria-hidden />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" aria-hidden />
    );
  };

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      <Table data-testid="mdm-devices-table" className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-16 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHeaderClick('status')}
                className="h-auto cursor-pointer text-sm font-medium text-muted-foreground"
                data-testid="mdm-devices-sort-status"
              >
                {'Status'}
                {renderSortIcon('status')}
              </Button>
            </TableHead>
            {visibleOrderIds.map((id) => {
              const col = columnMap[id];
              if (!col) return null;
              // Every column is sortable client-side (sort key = column id).
              const field = col.id as MdmDeviceSortField;
              return (
                <TableHead key={id} className={cn('truncate', col.width)} title={col.labelKey}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleHeaderClick(field)}
                    className="h-auto -ml-2 max-w-full cursor-pointer text-sm font-medium text-muted-foreground"
                    data-testid={`mdm-devices-sort-${col.id}`}
                  >
                    <span className="truncate">{col.labelKey}</span>
                    {renderSortIcon(field)}
                  </Button>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => {
            return (
              <TableRow
                key={device._id}
                data-testid="mdm-device-row"
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => onRowClick(device._id)}
              >
                <TableCell className="w-16 text-center">
                  <DeviceStatusDot
                    device={device}
                    liveStatus={device.UUID ? liveStatusByUuid?.get(device.UUID) : undefined}
                  />
                </TableCell>
                {visibleOrderIds.map((id) => {
                  const col = columnMap[id];
                  if (!col) return null;
                  return (
                    <TableCell key={id} className={cn('truncate', col.width)}>
                      {col.render(device)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
